var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import computeMunkres from "munkres-js";
import { bubbleUp } from "./utils";
/**
 * Dynamic optimal animated grid layout based on ratios
 */
export class Tiles {
    /**
     * @param options some initial options, can later change width .update
     */
    constructor(layout, options = {}) {
        this.layout = layout;
        this.boxes = [];
        this.ids = new Map();
        this.lastRemoved = null;
        this.lastLayout = { area: 1, rows: [0], positions: [] };
        this.rowsChanged = false;
        this.addedRendered = true;
        this.options = Object.assign({ margin: 10, width: document.body.clientWidth, height: document.body.clientHeight, top: 0, left: 0, focus: {
                maximize: false,
            } }, options);
    }
    contains(div) {
        return this.ids.has(div);
    }
    /**
     * position a box from another layout
     */
    tileBox(box) {
        this.ids.set(box.elm, box);
        this.boxes.push(box);
        bubbleUp(this.boxes, this.boxes.length - 1, (a, b) => a.ratio > b.ratio);
        this.calculate();
    }
    /**
     * position a new element in the tiles layout or update the ratio of one already in
     * @param div the box to control
     * @param ratio the ratio width/height to wish for
     */
    tile(div, ratio) {
        const match = this.ids.get(div);
        if (!match) {
            const box = {
                elm: div,
                ratio,
            };
            this.ids.set(div, box);
            this.boxes.push(box);
            bubbleUp(this.boxes, this.boxes.length - 1, (a, b) => a.ratio > b.ratio);
            this.addedRendered = false;
        }
        else if (match.ratio !== ratio) {
            match.ratio = ratio;
        }
        this.calculate();
    }
    /**
     * remove element from layout but not from dom
     * @param div
     */
    untile(div) {
        if (!this.ids.has(div)) {
            return;
        }
        this.lastRemoved = this.ids.get(div);
        this.layout.pendingRemoval.set(div, this.lastRemoved);
        this.ids.delete(div);
        this.boxes = this.boxes.filter(({ elm }) => !elm.isSameNode(div));
        this.calculate();
    }
    /**
     * mainly resize parent container, but also update top, left offset and margin
     * @param options update stuff, top and left are for offsetting
     */
    update(options) {
        this.options = Object.assign(Object.assign({}, this.options), options);
        this.calculate();
    }
    calculate() {
        const layout = this.findMaxAreaRows();
        this.makeItRound(layout);
        this.setPositions(layout);
        for (const box of this.boxes) {
            if (!box.state) {
                const old = this.layout.getRemovalPending(box.elm);
                box.elm.style.transitionProperty =
                    "top, left, height, width, visibility, opacity";
                box.elm.style.transitionTimingFunction = "ease";
                box.elm.style.transitionDuration = ".3s";
                box.state = old
                    ? old.state
                    : {
                        width: box.transition.width / 4,
                        height: box.transition.height / 4,
                        top: box.transition.top + (3 * box.transition.height) / 8,
                        left: box.transition.left + (3 * box.transition.width) / 8,
                        completed: false,
                    };
            }
        }
        if (this.lastRemoved) {
            this.lastRemoved.transition = {
                width: this.lastRemoved.state.width / 4,
                height: this.lastRemoved.state.height / 4,
                top: this.lastRemoved.state.top + (3 * this.lastRemoved.state.height) / 8,
                left: this.lastRemoved.state.left + (3 * this.lastRemoved.state.width) / 8,
                completed: false,
            };
            this.lastRemoved = null;
        }
        this.rowsChanged = this.lastLayout.rows.length !== layout.rows.length;
        this.lastLayout = layout;
    }
    render(animate = false) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.boxes.length) {
                return;
            }
            const hidden = this.options.focus.maximize && !!((_a = this.layout.focuser) === null || _a === void 0 ? void 0 : _a.tiles);
            const anim = !hidden && (!this.addedRendered || this.rowsChanged || animate);
            // set initial position for boxes
            for (const box of this.boxes) {
                if (!box.state.completed) {
                    box.elm.style.transitionDuration = ".0s";
                    box.elm.style.top = `${box.state.top}px`;
                    box.elm.style.left = `${box.state.left}px`;
                    box.elm.style.width = `${box.state.width}px`;
                    box.elm.style.height = `${box.state.height}px`;
                    box.state.completed = true;
                }
            }
            // give some time for transition to register initial position
            yield new Promise((resolve) => setTimeout(resolve, 0));
            for (const box of this.boxes) {
                box.elm.style.transitionDuration = anim ? "300ms" : ".0s";
                box.elm.style.top = `${box.transition.top}px`;
                box.elm.style.left = `${box.transition.left}px`;
                box.elm.style.width = `${box.transition.width}px`;
                box.elm.style.height = `${box.transition.height}px`;
                box.elm.style.visibility = hidden ? "hidden" : "visible";
                box.transition.completed = true;
                box.state = box.transition;
            }
            if (anim)
                yield new Promise((resolve) => setTimeout(resolve, 300));
            this.addedRendered = true;
        });
    }
    /**
     * erase previously calculated positions, aka make it fresh
     */
    reset() {
        this.lastLayout.area = 1;
    }
    // find generic layout for rowCount rows
    getRowsRatios(rowCount) {
        // find row dimensions without margin
        const height = (this.options.height - (rowCount + 1) * this.options.margin) / rowCount;
        const width = this.options.width;
        // put descending boxes in rows with descending width
        const rows = [...Array(rowCount)].map(() => ({
            sum: 0,
            count: 0,
            ratios: {},
        }));
        for (const box of this.boxes) {
            const row = rows[rows.length - 1];
            row.ratios[box.ratio] = row.ratios[box.ratio] + 1 || 1;
            row.sum += box.ratio;
            row.count++;
            bubbleUp(rows, rows.length - 1, (a, b) => a.sum > b.sum);
        }
        const rowMargin = this.options.margin * (rows[0].count + 1);
        // find common height for all videos
        const rowHeight = rows[0].sum * height + rowMargin > width
            ? (width - rowMargin) / rows[0].sum
            : height;
        // H*all_ratios + (rows+1)*margin = total_width <= width
        // H <= height
        // find area of all videos
        const maxArea = this.boxes
            .map(({ ratio }) => rowHeight * rowHeight * ratio)
            .reduce((a, b) => a + b);
        return {
            area: maxArea,
            height: rowHeight,
            rows,
        };
    }
    /**
     * calculate positions or boxes in container to maximize their area
     */
    findMaxAreaRows() {
        let maxLayout = { area: 0, rows: [] };
        let updatedLayout = null;
        for (let rows = 1; rows <= this.boxes.length; rows++) {
            const layout = this.getRowsRatios(rows);
            if (this.lastLayout.rows.length === rows) {
                updatedLayout = layout;
            }
            // if greater than max, set as new row
            if (layout.area > maxLayout.area) {
                maxLayout = layout;
            }
        }
        return !updatedLayout || maxLayout.area / updatedLayout.area > 1.02
            ? maxLayout
            : updatedLayout;
    }
    makeItRound(layout) {
        const rows = [];
        // sort smaller at edges, biggest at center
        while (layout.rows.length) {
            rows.push(layout.rows.shift());
            if (!layout.rows.length) {
                break;
            }
            rows.unshift(layout.rows.shift());
        }
        layout.rows = rows;
    }
    setPositions(layout) {
        const positions = [];
        const rowY0 = (this.options.height -
            ((layout.rows.length + 1) * this.options.margin +
                layout.rows.length * layout.height)) /
            2 +
            this.options.margin;
        const rowStatus = [];
        // find approximate positions for the boxes
        for (let r = 0; r < layout.rows.length; r++) {
            const rowY = r * (layout.height + this.options.margin) + rowY0;
            const rowRatio = layout.rows[r].sum;
            const rowWidth = rowRatio * layout.height +
                (layout.rows[r].count - 1) * this.options.margin;
            let boxX = (this.options.width - rowWidth) / 2;
            rowStatus.push({ rowWidth, rowX0: boxX, size: layout.rows[r].count });
            const avgWidth = rowWidth / layout.rows[r].count;
            let column = 0;
            for (let times of Object.values(layout.rows[r].ratios)) {
                while (times--) {
                    positions.push({
                        row: r,
                        column: column++,
                        top: rowY,
                        left: boxX,
                    });
                    boxX += avgWidth;
                }
            }
        }
        const matched = new Set();
        for (let c = 0; matched.size !== this.boxes.length && c < this.boxes.length; c++) {
            const distances = [];
            const mapY = [];
            const mapX = [];
            for (const pos of positions) {
                if (pos.column < c) {
                    continue;
                }
                mapY.push(pos);
                const row = [];
                for (const box of this.boxes) {
                    if (matched.has(box)) {
                        continue;
                    }
                    mapX.push(box);
                    const state = box.state || { top: 10000, left: 1000 };
                    row.push(box.ratio in layout.rows[pos.row].ratios
                        ? Math.sqrt(Math.pow(pos.left - state.left, 2) +
                            Math.pow(pos.top - state.top, 2))
                        : 100000000000000);
                }
                distances.push(row);
            }
            const matches = computeMunkres(distances);
            for (let y = 0; y < mapY.length; y++) {
                const position = mapY[y];
                if (position.column === c) {
                    const match = matches.find(([indexY]) => y === indexY);
                    const box = match
                        ? mapX[match[1]]
                        : this.boxes.find((box) => !box.transition &&
                            box.ratio in layout.rows[position.row].ratios);
                    if (!box) {
                        continue;
                    }
                    matched.add(box);
                    box.transition = {
                        top: this.options.top + position.top,
                        left: this.options.left + position.left,
                        width: layout.height * box.ratio,
                        height: layout.height,
                        completed: false,
                    };
                    rowStatus[position.row].rowWidth -=
                        box.transition.width + this.options.margin;
                    rowStatus[position.row].rowX0 +=
                        box.transition.width + this.options.margin;
                    rowStatus[position.row].size--;
                    rowStatus[position.row].avgWidth =
                        rowStatus[position.row].rowWidth /
                            (rowStatus[position.row].size || 1);
                    layout.rows[position.row].ratios[box.ratio]--;
                    if (layout.rows[position.row].ratios[box.ratio] === 0) {
                        delete layout.rows[position.row].ratios[box.ratio];
                    }
                }
                else {
                    position.left =
                        rowStatus[position.row].rowX0 +
                            rowStatus[position.row].avgWidth * (position.column - c - 1);
                }
            }
        }
    }
}
